import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import PostAddOutlinedIcon from '@material-ui/icons/PostAddOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import VisibilityIcon from '@material-ui/icons/Visibility';
import {
  ChatContent,
  ChipFilters,
  ConversationsList,
  DrawerTemplates,
  MultipleLineSelect,
  SearchContactsDrawer,
  SearchResults,
  SelectConversations,
  StyledIcon
} from 'features/message-center';
import { useChatV2, useTextConversations } from 'features/message-center/hooks';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import { FPIconButton, When } from 'UI/components/atoms';
import { FPButtonMenu, Searchbar } from 'UI/components/molecules';
import DualPanelLayout from 'UI/components/templates/DualPanelLayout/DualPanelLayout';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { UIStatus } from 'UI/constants/status';
import { formatInternationalPhone } from 'UI/utils';
import { getEntityType } from 'UI/utils/inventory';

export const MessageCenter = () => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const [searchKeyword, setSearchKeyword] = useState('');

  const canSelectConversations = checkIfFeatureFlagEnabled(
    FeatureFlags.MessageCenterSelectMessages
  );
  const showFilters = checkIfFeatureFlagEnabled(FeatureFlags.MessageCenterFilters);

  const {
    conversations,
    filterParams,
    handleChangeFilterParams,
    handleClickConversation,
    handleClickConversationCheckbox,
    handleCloseSearchContactsDrawer,
    handleFetchMoreConversations,
    handleMarkConversations,
    handleMessageChange,
    handleOpenSearchContactsDrawer,
    handleRefresh,
    handleSearch,
    handleUpdateConversation,
    selectedConversation,
    selectedConversations,
    toggleQuickViewOpen,
    toggleSelectConversations,
    toggleTemplates,
    uiState
  } = useTextConversations();

  const {
    chat,
    chatStatus,
    handleClickRetry,
    handleClickSend,
    handleFetchMoreMessages,
    handleRefreshConversation,
    shouldScrollToBottom
  } = useChatV2({
    externalNumber: selectedConversation?.externalNumber,
    selectedConversationInList: selectedConversation,
    onUpdateConversation: handleUpdateConversation
  });

  const isEntityTypeValid = !!getEntityType(selectedConversation?.entityType);

  const handleAddNewConversation = ({ externalNumber }) => handleRefresh(externalNumber);
  const handleSearchChange = (_name, value) => setSearchKeyword(value);
  const handleSearchAsync = () => handleSearch(searchKeyword.trim());

  const menuItems = [
    {
      title: strings.messageCenter.messageComposer.templates.viewTemplates,
      icon: (
        <StyledIcon>
          <PostAddOutlinedIcon fontSize="small" />
        </StyledIcon>
      ),
      action: toggleTemplates,
      visible: checkIfFeatureFlagEnabled(FeatureFlags.MessageCenterTemplates)
    },
    {
      title: strings.messageCenter.selectConversations,
      icon: (
        <StyledIcon>
          <CheckBoxOutlinedIcon fontSize="small" />
        </StyledIcon>
      ),
      action: () => toggleSelectConversations(!uiState.showSelectConversations),
      visible: canSelectConversations && !uiState.isSearching
    }
  ];

  const getFinalContent = () => {
    if (uiState.isSearching) {
      return (
        <SearchResults
          results={conversations}
          isLoading={uiState.status === UIStatus.Loading}
          customMessageKey="messageId"
          selectedConversationId={selectedConversation?.messageId}
          onClickConversation={handleClickConversation}
        />
      );
    }

    return (
      <>
        <When condition={uiState.showSelectConversations}>
          <SelectConversations
            onClose={() => toggleSelectConversations(false)}
            count={selectedConversations.length}
            onClickMarkConversations={handleMarkConversations}
          />
        </When>
        <ConversationsList
          conversations={conversations}
          isLoading={uiState.status === UIStatus.Loading}
          onClickCheckbox={handleClickConversationCheckbox}
          onClickConversation={handleClickConversation}
          onFetchMoreConversations={handleFetchMoreConversations}
          searchKeyword={searchKeyword}
          selectedConversationId={selectedConversation?.id}
          selectedConversations={selectedConversations}
          showCheckbox={uiState.showSelectConversations}
        />
      </>
    );
  };

  return (
    <>
      <DualPanelLayout
        storageKey={PERSISTENCE_KEYS.LAYOUT_WIDTHS.MESSAGE_CENTER}
        leftPanel={{
          header: {
            title: strings.messageCenter.title,
            Actions: (
              <Box display="flex" alignItems="center">
                <FPIconButton
                  size={24}
                  onClick={handleOpenSearchContactsDrawer}
                  tooltipProps={{
                    title: strings.messageCenter.newMessage
                  }}
                >
                  <AddCircleIcon color="primary" />
                </FPIconButton>
                <FPIconButton
                  onClick={handleRefresh}
                  tooltipProps={{
                    title: strings.messageCenter.refreshConversations
                  }}
                >
                  <RefreshIcon />
                </FPIconButton>
                <FPButtonMenu
                  disabled={uiState.isLoading}
                  isIconButton
                  menuItems={menuItems}
                  title={strings.shared.rowActions.more}
                />
              </Box>
            ),
            Header: (
              <>
                <MultipleLineSelect
                  enablePadding
                  onSelectLine={() => toggleSelectConversations(false)}
                />
                <Searchbar
                  name="keyword"
                  value={searchKeyword}
                  placeholder={strings.shared.ui.search}
                  width="auto"
                  onChange={handleSearchChange}
                  onSearchAsync={handleSearchAsync}
                  showClearButton
                />
                <When
                  condition={
                    showFilters && !uiState.isSearching && !uiState.showSelectConversations
                  }
                >
                  <ChipFilters
                    filterParams={filterParams}
                    onClickFilter={handleChangeFilterParams}
                  />
                </When>
              </>
            )
          },
          Content: getFinalContent()
        }}
        rightPanel={{
          header: {
            Header: selectedConversation ? (
              <>
                <Typography variant="h5">{selectedConversation?.fullName || ''}</Typography>
                <Typography variant="body2">
                  {formatInternationalPhone(selectedConversation?.externalNumber || '')}
                </Typography>
              </>
            ) : null,
            Actions: selectedConversation ? (
              <>
                <FPIconButton
                  onClick={handleRefreshConversation}
                  tooltipProps={{
                    title: strings.messageCenter.chat.refresh
                  }}
                >
                  <RefreshIcon />
                </FPIconButton>
                <When condition={isEntityTypeValid}>
                  <FPIconButton
                    onClick={toggleQuickViewOpen}
                    tooltipProps={{
                      title: strings.inventoryProfiles.quickView.viewProfile
                    }}
                  >
                    <VisibilityIcon />
                  </FPIconButton>
                </When>
              </>
            ) : null
          },
          Content: (
            <ChatContent
              chat={chat}
              chatStatus={chatStatus}
              isQuickViewOpen={uiState.isQuickViewOpen}
              onClickRetry={handleClickRetry}
              onClickSend={handleClickSend}
              onFetchMoreMessages={handleFetchMoreMessages}
              onMessageChange={handleMessageChange}
              selectedConversationInList={selectedConversation}
              shouldScrollToBottom={shouldScrollToBottom}
              toggleQuickViewOpen={toggleQuickViewOpen}
            />
          )
        }}
      />

      {uiState.isSearchContactsDrawerOpen && (
        <SearchContactsDrawer
          isDrawerOpen={uiState.isSearchContactsDrawerOpen}
          initialPhoneNumber={uiState.newConversationPhoneNumber}
          onClose={handleCloseSearchContactsDrawer}
          onNewConversation={handleAddNewConversation}
        />
      )}

      <When condition={uiState.openTemplates}>
        <DrawerTemplates open={uiState.openTemplates} toggleTemplates={toggleTemplates} />
      </When>
    </>
  );
};
